import { algoliasearch } from 'algoliasearch';
import { UploadFile } from 'antd';
import { unstable_cache } from 'next/cache';
import { z } from 'zod';

import { directusService, zodKeys } from '@/services/api.service';
import { getRevalidateTimeout } from '@/utils/cache';

const SellYourCarSchema = z.object({
  main_image: z.string().optional().nullish(),
  header_title: z.string().optional().nullish(),
  header_content: z.string().optional().nullish(),
  header_button_text: z.string().optional().nullish(),
  call_to_action: z
    .object({
      text: z.string().default(''),
      url: z.string().default(''),
      type: z.string().default(''),
      heading: z.string().optional().nullish(),
      subheading: z.string().optional().nullish(),
      image: z.string().optional().nullish(),
      icon: z.string().optional().nullish(),
      status: z.string().optional().nullish(),
    })
    .optional()
    .nullish(),
  footer_call_to_action: z
    .object({
      text: z.string().default(''),
      url: z.string().default(''),
      type: z.string().default(''),
      heading: z.string().optional().nullish(),
      subheading: z.string().optional().nullish(),
      image: z.string().optional().nullish(),
      icon: z.string().optional().nullish(),
      status: z.string().optional().nullish(),
    })
    .optional()
    .nullish(),
  process_steps: z
    .array(
      z.object({
        number: z.number(),
        title: z.string().default(''),
        description: z.string().default(''),
      }),
    )
    .default([]),
});

export const getSellYourCarPageData = async () => {
  const { data } = await directusService.get('/items/sell_your_car_page', {
    params: {
      fields: zodKeys(SellYourCarSchema),
    },
  });

  const result = SellYourCarSchema.safeParse(data);

  if (!result.success) return null;
  return result.data;
};

export type SellYourType = Awaited<ReturnType<typeof getSellYourCarPageData>>;
export type ProcessStep =
  z.infer<(typeof SellYourCarSchema.shape)['process_steps']> extends
    | (infer R)[]
    | null
    | undefined
    ? R
    : never;
export const fetchSellYouarCarData = unstable_cache(
  getSellYourCarPageData,
  ['sell_your_car_page'],
  {
    tags: ['sell_your_car_page'],
    revalidate: getRevalidateTimeout(86400),
  },
);

export interface SellYourCarPageData {
  bannerImage: string;
  heading: string;
  description: string;
  imageKeys: {
    desktopKey: string;
    mobileKey: string;
  };
}

export type TransformedRange = {
  title: string;
  value: string;
  key: string;
  selectable: boolean;
  rangeId: number;
  slug: string;
};

export type TransformedModel = {
  title: string;
  value: string;
  key: string;
  selectable: boolean;
  modelId: string;
  slug: string;
  price?: string;
  'fuel-type'?: string;
  transmission?: string;
  'driven-wheels'?: string;
  'engine-detail'?: string;
  doors?: string;
  'engine-capacity-cc'?: string;
  [key: string]: any;
};

export type TransformedBrand = {
  title: string;
  value: string;
  key: string;
  selectable: boolean;
  disabled: boolean;
  brandId: number;
  slug: string;
  children: TransformedRange[];
};

type AlgoliaRangeItem = {
  name: string;
  slug: string;
  brandId: number;
  brandName: string;
  brandSlug: string;
  objectID: string;
  _highlightResult: object;
};

export type SellYourFormValues = {
  vehicle_year: string;
  vehicle_brand: string;
  vehicle_range: string;
  vehicle_model: string;
  vehicle_fuel: string;
  vehicle_transmission: string;
  vehicle_kms: string | null;
  vehicle_state: string;
  photos: UploadFile[];
  message: string;
  email: string;
  phone_number: string;
  user_name: string;
  vehicle_expected_price: string;
  custom_vehicle_brand: {
    value: string;
    label?: string;
  };
  province: string;
};

const algoliaClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '',
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY || '',
);

async function fetchRangesFromAlgolia(): Promise<AlgoliaRangeItem[]> {
  try {
    const result = await algoliaClient.searchSingleIndex<AlgoliaRangeItem>({
      indexName: 'ranges',
      searchParams: {
        hitsPerPage: 1000,
      },
    });

    return result.hits || [];
  } catch (error) {
    console.error('Failed to fetch ranges from Algolia:', error);
    return [];
  }
}

function transformAlgoliaData(ranges: AlgoliaRangeItem[]): TransformedBrand[] {
  const brandMap = new Map<
    number,
    {
      name: string;
      slug: string;
      ranges: Map<
        string,
        {
          name: string;
          slug: string;
          id: number;
        }
      >;
    }
  >();

  ranges.forEach((range) => {
    if (!brandMap.has(range.brandId)) {
      brandMap.set(range.brandId, {
        name: range.brandName,
        slug: range.brandSlug,
        ranges: new Map(),
      });
    }

    const brand = brandMap.get(range.brandId);

    if (brand && !brand.ranges.has(range.name)) {
      const rangeId = parseInt(range.objectID);

      brand.ranges.set(range.name, {
        name: range.name,
        slug: range.slug,
        id: rangeId,
      });
    }
  });

  let transformedBrands = Array.from(brandMap.entries()).map(
    ([brandId, brand]) => ({
      title: brand.name,
      value: brand.name,
      key: brand.name,
      selectable: false,
      disabled: true,
      brandId: brandId,
      slug: brand.slug,
      customTitle: brand.name,
      children: Array.from(brand.ranges.values())
        .sort((a, b) => a?.name.localeCompare(b?.name))
        .map((range) => {
          return {
            title: range.name,
            value: `${brand.name}|${range.name}|${range?.id}`,
            key: `${brand.name}|${range.name}|${range?.id}`,
            selectable: true,
            rangeId: range.id,
            slug: range.slug,
            customTitle: `${brand.name} | ${range.name}`,
          };
        }),
    }),
  );

  transformedBrands.sort((a, b) => {
    if (a.title.toLowerCase() === 'kia') return -1;
    if (b.title.toLowerCase() === 'kia') return 1;
    return a.title.localeCompare(b.title);
  });

  return transformedBrands;
}

export const fetchAndCacheMakeAndModel = unstable_cache(
  async (): Promise<TransformedBrand[]> => {
    try {
      const ranges = await fetchRangesFromAlgolia();

      const transformedData = transformAlgoliaData(ranges);

      return transformedData;
    } catch (error) {
      console.error('Failed to fetch brands and models:', error);
      return [];
    }
  },
  ['make-and-model-data'],
  {
    revalidate: 24 * 60 * 60,
    tags: ['make-and-model-cache'],
  },
);

type AlgoliaModelItem = {
  name: string;
  slug: string;
  brandId: number;
  brandName: string;
  brandSlug: string;
  rangeId: number;
  rangeName: string;
  rangeSlug: string;
  objectID: string;
  model: string;
  price?: string;
  'intro-date'?: string;
  'end-date'?: string;
  'fuel-type'?: string;
  transmission?: string;
  gearshift?: string;
  'driven-wheels'?: string;
  'engine-detail'?: string;
  doors?: string;
  'engine-capacity-cc'?: string;
  [key: string]: any;
  _highlightResult: object;
};

export async function fetchModelsByRangeId(rangeId: number): Promise<TransformedModel[]> {
  try {
    if (isNaN(rangeId) || rangeId <= 0) return [];

    const result = await algoliaClient.searchSingleIndex<AlgoliaModelItem>({
      indexName: 'models',
      searchParams: {
        hitsPerPage: 1000,
        filters: `rangeId = ${rangeId}`,
      },
    });

    if (!result.hits?.length) return [];

    const models: TransformedModel[] = result.hits.map((model) => ({
      title: model.name || model.model,
      value: model.model || model.name,
      key: model.objectID,
      selectable: true,
      modelId: model.objectID,
      slug: model.slug,
      price: model.price,
      'fuel-type': model['fuel-type'],
      transmission: model.gearshift || model.transmission,
      'driven-wheels': model['driven-wheels'],
      'engine-detail': model['engine-detail'],
      doors: model.doors,
      'engine-capacity-cc': model['engine-capacity-cc'],
    }));

    const uniqueModels = Array.from(
      new Map(models.map((m) => [m.slug || m.value, m])).values()
    );

    return uniqueModels;
  } catch (error) {
    console.error(`Failed to fetch models for rangeId ${rangeId}:`, error);
    return [];
  }
}
